import { Component, CUSTOM_ELEMENTS_SCHEMA, input, OnDestroy, OnInit, output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { translate } from '@jsverse/transloco';

import { Subject, takeUntil } from 'rxjs';

import { AppSharedModule } from 'app/app-shared.module';
import { Radio } from 'app/models/radio';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import {
  ConfigurationItemListOptions,
  ConfigurationItemListOptionsFilter,
  ConfigurationItemListOptionsSort,
} from '../../models/configuration-item-list-options.model';

interface OptionsForm {
  sort: FormControl<ConfigurationItemListOptionsSort>;
  filter: FormControl<ConfigurationItemListOptionsFilter>;
  showHidden: FormControl<boolean>;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-configuration-item-editor-options-popout',
  imports: [AppSharedModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './configuration-item-editor-options-popout.component.html',
  styleUrls: ['./configuration-item-editor-options-popout.component.scss'],
})
export class ConfigurationItemEditorOptionsPopoutComponent implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();

  readonly options = input.required<ConfigurationItemListOptions>();

  readonly optionsChange = output<ConfigurationItemListOptions>();

  form: FormGroup<OptionsForm>;

  readonly sortRadios: Radio<ConfigurationItemListOptionsSort>[] = [
    {
      label: translate('configurations.configuration-item-editor-options-popout.sort.name'),
      value: 'name',
    },
    {
      label: translate('configurations.configuration-item-editor-options-popout.sort.date'),
      value: 'date',
    },
  ];

  readonly filterRadios: Radio<ConfigurationItemListOptionsFilter>[] = [
    {
      label: translate('configurations.configuration-item-editor-options-popout.filter.both'),
      value: 'both',
    },
    {
      label: translate('configurations.configuration-item-editor-options-popout.filter.name'),
      value: 'name',
    },
    {
      label: translate('configurations.configuration-item-editor-options-popout.filter.query'),
      value: 'query',
    },
  ];

  private readonly unsub$ = new Subject<void>();

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.buildForm();
  }

  ngOnDestroy(): void {
    this.unsub$.next();
    this.unsub$.complete();
  }

  private buildForm(): void {
    this.form = this.formBuilder.group(<OptionsForm>{
      sort: new FormControl(this.options().sort),
      filter: new FormControl(this.options().filter),
      showHidden: new FormControl(this.options().showHidden),
    });

    this.form.valueChanges.pipe(takeUntil(this.unsub$)).subscribe(() => {
      const rawValue = this.form.getRawValue();
      this.optionsChange.emit(
        new ConfigurationItemListOptions(rawValue.sort, rawValue.filter, rawValue.showHidden)
      );
    });
  }
}
